import {
  bindService,
  createServiceToken,
  type AppFeatureDefinition,
  type ServiceBinding,
} from "@nasam/kernel";
import {
  catalogRepositoryToken,
  companiesRepositoryToken,
  type CatalogRepository,
  type CompaniesRepository,
} from "../../contracts";
import { createSubscriptionsHttpRepository } from "./data";
import type { SubscriptionsRepository } from "./domain";
import { createSubscriptionRoutes } from "./presentation/routes";
import { SUBSCRIPTIONS_VIEW } from "./subscriptionsPermissions";

export * from "./subscriptionsPermissions";
export const subscriptionsRepositoryToken =
  createServiceToken<SubscriptionsRepository>(
    "subscriptions.subscriptions-repository",
  );
function bindings(
  httpClient: Parameters<
    NonNullable<AppFeatureDefinition["provideServices"]>
  >[0]["httpClient"],
): readonly ServiceBinding[] {
  return [
    bindService(
      subscriptionsRepositoryToken,
      createSubscriptionsHttpRepository(httpClient),
    ),
  ];
}
export const subscriptionsFeature: AppFeatureDefinition = {
  id: "subscriptions",
  version: "0.1.0",
  titleKey: "subscriptions:moduleTitle",
  icon: "ReceiptText",
  basePath: "/subscriptions",
  requiredPermissions: [SUBSCRIPTIONS_VIEW],
  // Every CORE feature shares one "core" rail node (see mergeFeatureNavNodes);
  // this feature contributes the "Subscriptions" leaf under it.
  navigation: [
    {
      id: "core",
      titleKey: "common:businessModules.core",
      icon: "Layers",
      placement: "rail",
      order: 10,
      children: [
        {
          id: "subscriptions",
          titleKey: "subscriptions:moduleTitle",
          icon: "ReceiptText",
          placement: "drawer",
          // Moved up from 100 to sit right after Catalog, matching the
          // current design's drawer order (see catalogFeature's order
          // comment for why this is a non-round value).
          order: 14,
          path: "/subscriptions",
          requiredPermissions: [SUBSCRIPTIONS_VIEW],
        },
      ],
    },
  ],
  routes: (context) =>
    createSubscriptionRoutes({
      context,
      subscriptions: context.services.resolve(subscriptionsRepositoryToken),
      companies: context.services.tryResolve<CompaniesRepository>(
        companiesRepositoryToken,
      ),
      catalog: context.services.tryResolve<CatalogRepository>(
        catalogRepositoryToken,
      ),
    }),
  i18n: {
    namespace: "subscriptions",
    load: {
      en: () => import("../../i18n/locales/en/subscriptions"),
      ar: () => import("../../i18n/locales/ar/subscriptions"),
    },
  },
  permissionCatalog: [
    {
      segment: "core",
      labelKey: "subscriptions:permissions.core",
      children: [
        {
          segment: "subscription_management",
          labelKey: "subscriptions:permissions.management",
          children: [
            {
              segment: "subscription_management_list",
              labelKey: "subscriptions:permissions.subscriptions",
              actions: [
                { id: "view", labelKey: "subscriptions:actions.view" },
                { id: "create", labelKey: "subscriptions:actions.create" },
                { id: "update", labelKey: "subscriptions:actions.update" },
                { id: "delete", labelKey: "subscriptions:actions.delete" },
              ],
            },
          ],
        },
      ],
    },
  ],
  provideServices: ({ httpClient }) => bindings(httpClient),
};
